import { useTheme } from '@emotion/react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import { TABLE_DATA_TYPES } from 'staticData';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Tooltip, tooltipClasses } from '@mui/material';
import { styled } from '@mui/material/styles';

import format from 'shared/lib/date-fns/format';
import { useTransl } from 'shared/lib/i18n';

import AsyncTableContainer from '../../Table/AsyncTableContainer';
import AddRsvpContainer from '../containers/AddRsvpContainer';

import ExpandableImageComponent from '../../Image/ExpandableImageComponent';
import ModalHandlerComponent from '../../Modal/ModalHandlerComponent';
import AdditionalGuestsComponent from './AdditionalGuestsComponent';

import { confirmDeleteRsvp } from '../actions';

import { avatarPlaceholderSRC, toSentenceCase } from '../../../utils';

import { TABLE_CONSTANTS, TABLE_ID } from '../../Table/constants';

import ListActions from '../../Table/ListActions';

const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    marginBottom: '-3px !important',
  },
}));

const EventRsvpsComponent = ({
  listParams,
  items,
  getItems,
  onLoad,
  listKey,
  queryParams,
  eventId,
}) => {
  const { t } = useTransl();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleClose = () => setIsModalOpen(false);

  const columns = [
    {
      expander: true,
      isExpander: true,
      id: 'expander_columns',
      sortable: false,
      width: 30,
      // eslint-disable-next-line react/prop-types
      Expander: ({ isExpanded, original: { guests } }) => {
        // eslint-disable-next-line react/prop-types
        const isGuests = !!guests?.length;
        let tooltipTitle = isGuests && t('title.expandMore');
        let icon = isGuests && <ExpandMoreIcon />;

        if (isExpanded && isGuests) {
          icon = <ExpandLessIcon />;
          tooltipTitle = t('title.expandLess');
        }

        return (
          <StyledTooltip title={tooltipTitle} placement="top" arrow>
            <Box
              role="presentation"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                height: '5rem',
              }}
            >
              {icon}
            </Box>
          </StyledTooltip>
        );
      },
    },
    {
      Header: t('title.guest'),
      id: 'guest',
      sortable: false,
      accessor: ({ attendee: { fullName, photo } }) => (
        <Box display="flex" alignItems="center">
          <Box mr="1rem">
            <ExpandableImageComponent
              src={avatarPlaceholderSRC}
              photo={photo}
              caption={fullName}
              height="3.2rem"
              isSquare
            />
          </Box>
          <Box className="crop-long-text">{fullName}</Box>
        </Box>
      ),
    },
    {
      Header: t('title.property'),
      id: 'property',
      sortable: false,
      accessor: ({ attendee: { property } }) =>
        property?.name ?? t('common.NA'),
    },
    {
      Header: t('title.role'),
      id: 'role',
      sortable: false,
      accessor: ({ attendee: { role } }) =>
        role ? toSentenceCase(role?.name) : t('title.guest'),
    },
    {
      Header: t('title.rsvpOn'),
      id: 'created_at',
      sortable: true,
      headerClassName: 'sort',
      accessor: ({ createdAt }) => format(parseISO(createdAt), 'MM/dd/yyyy'),
    },
    {
      Header: t('title.action'),
      id: 'action',
      sortable: false,
      width: 120,
      accessor: ({ id }) => {
        const actions = [
          {
            label: t('actions.remove'),
            color: theme.palette.secondary.main,
            onClick: () => confirmDeleteRsvp(dispatch, id, queryParams),
          },
        ];

        return (
          <ListActions
            color={theme.palette.primary.main}
            actions={actions}
            isFullWidth
          />
        );
      },
    },
  ];

  const filters = [];

  const defaultParams = {
    ordering: '-created_at',
    limit: 25,
    offset: 0,
    building_id: null,
  };

  const action = [
    {
      label: t('events.addRsvp'),
      onClick: () => setIsModalOpen(true),
    },
  ];

  return (
    <>
      {isModalOpen && (
        <ModalHandlerComponent
          modal={{
            modalType: 'CUSTOM',
            modalProps: {
              title: t('events.addRsvps'),
              maxWidth: 'sm',
            },
          }}
          onModalClose={handleClose}
        >
          <AddRsvpContainer
            eventId={eventId}
            setIsModalOpen={setIsModalOpen}
            queryParams={queryParams}
            handleClose={handleClose}
          />
        </ModalHandlerComponent>
      )}
      <AsyncTableContainer
        tableId={TABLE_ID.eventRsvpsTable}
        title={t('events.rsvps')}
        columns={columns}
        defaultSort={TABLE_CONSTANTS.created_at}
        defaultSortDesc
        manual
        filters={filters}
        showFiltersAreApplied={false}
        onLoad={onLoad}
        items={items}
        getItems={getItems}
        listParams={listParams}
        defaultParams={defaultParams}
        tableDataType={TABLE_DATA_TYPES.person}
        listKey={listKey}
        action={<ListActions actions={action} />}
        searchAutoFocus
        SubComponent={(props) =>
          // eslint-disable-next-line react/prop-types
          !!props?.original?.guests?.length && (
            <AdditionalGuestsComponent
              {...props}
              dispatch={dispatch}
              theme={theme}
              queryParams={queryParams}
            />
          )
        }
      />
    </>
  );
};

EventRsvpsComponent.propTypes = {
  listParams: PropTypes.instanceOf(Object),
  items: PropTypes.instanceOf(Object),
  getItems: PropTypes.func.isRequired,
  onLoad: PropTypes.func.isRequired,
  listKey: PropTypes.string.isRequired,
  queryParams: PropTypes.instanceOf(Object).isRequired,
  buildings: PropTypes.instanceOf(Object),
  eventId: PropTypes.string.isRequired,
};

export default EventRsvpsComponent;
